import { type ReactElement, type ReactNode, type RefObject, useState } from 'react'
import useEvent from 'react-use-event-hook'
import type { TooltipPositionerProps } from '@base-ui/react/tooltip'
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'

import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { ChevronDownIcon } from '@gto/ui/icons/outline'
import { Popover } from '@gto/ui/popover'
import { typography } from '@gto/ui/typography'

import { useCursorAnchor } from './use-cursor-anchor'

export type TooltipProps = {
  children: ReactElement
  content: ReactNode
  className?: string
  placement?: TooltipPositionerProps['side']
  align?: TooltipPositionerProps['align']
  open?: boolean
  disabled?: boolean
  delay?: number
  closeDelay?: number
  /**
   * Whether clicking the trigger closes the tooltip. Defaults to Base UI's
   * `true`; set to `false` when the trigger has its own click handler that
   * should not race with Base UI's auto-close. When on, an uncontrolled
   * tooltip also stays closed under the resting pointer after the click
   * (released on pointer leave / blur); controlled `open` is never affected.
   */
  closeOnClick?: boolean
  /**
   * Whether the mobile popover trigger renders native button semantics. The
   * legacy default treats composite children as native buttons; set `false`
   * when a composite child renders a non-native control such as `Chip`.
   */
  mobileTriggerIsNativeButton?: boolean
  followCursor?: boolean
  sideOffset?: number
  arrow?: boolean
  hoverable?: boolean
  anchor?: RefObject<Element | null>
  onOpenChange?: (open: boolean) => void
}

export const Tooltip = ({
  children,
  content,
  className,
  placement = 'top',
  align,
  open,
  disabled,
  delay = 300,
  closeDelay,
  closeOnClick,
  mobileTriggerIsNativeButton,
  sideOffset = 8,
  followCursor,
  arrow,
  hoverable,
  anchor,
  onOpenChange,
}: TooltipProps) => {
  const isDisabled = disabled === true || content == null
  // A click closes the tooltip, but the pointer still resting on the trigger
  // would immediately re-open it — over whatever the click just revealed.
  // Latch closed until the pointer leaves (or focus moves), unless the
  // consumer opted out of close-on-click entirely.
  const [clickLatched, setClickLatched] = useState(false)
  const latchesOnClick = closeOnClick !== false && open === undefined
  const armClickLatch = useEvent(() => {
    if (latchesOnClick) {
      setClickLatched(true)
    }
  })
  const releaseClickLatch = useEvent(() => {
    if (clickLatched) {
      setClickLatched(false)
    }
  })
  const popoverTriggerIsButton =
    mobileTriggerIsNativeButton ?? (typeof children.type !== 'string' || children.type === 'button')
  const isFollowCursor = followCursor === true
  const isMobile = useMediaQuery('down', 'sm')
  const [mobileOpen, setMobileOpen] = useState(false)

  const cursorAnchor = useCursorAnchor(isFollowCursor)

  // When following the cursor, drive positioning through a virtual anchor
  // that always reports the latest pointer position, and disable Base UI's
  // own cursor tracking — its `useClientPoint` integration caches the
  // previous position across opens and ships it as the first frame of the
  // next open. The virtual anchor sidesteps that.
  const positionerAnchor = isFollowCursor ? cursorAnchor.virtualElement : anchor

  const handleOpenChange = useEvent((nextOpen: boolean) => {
    if (isMobile && open === undefined) {
      setMobileOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  })

  if (isMobile && !isDisabled) {
    return (
      <Popover.Root
        open={open ?? mobileOpen}
        onOpenChange={handleOpenChange}
      >
        <Popover.Trigger
          nativeButton={popoverTriggerIsButton}
          render={children}
          onClick={
            closeOnClick === false
              ? (event) => {
                  event.preventDefault()
                }
              : undefined
          }
        />
        <Popover.Portal>
          <Popover.Positioner
            align={align}
            anchor={anchor}
            className="z-tooltip"
            side={placement}
            sideOffset={sideOffset}
          >
            <Popover.Popup
              className={cn(
                typography['tooltip/label'],
                'max-w-80 rounded-sm border border-secondary-main bg-paper-elevation4 p-2 text-text-primary shadow-8',
                className,
              )}
            >
              {content}
              {arrow === true && (
                <Popover.Arrow className="flex data-[side=bottom]:top-[-11px] data-[side=left]:right-[-11px] data-[side=right]:left-[-11px] data-[side=top]:bottom-[-11px] data-[side=bottom]:rotate-180 data-[side=left]:-rotate-90 data-[side=right]:rotate-90 data-[side=top]:rotate-0">
                  <ChevronDownIcon className="size-[20px] text-secondary-main" />
                </Popover.Arrow>
              )}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    )
  }

  return (
    <BaseTooltip.Root
      disableHoverablePopup={hoverable !== true}
      // The latch only governs UNCONTROLLED tooltips: Base UI computes
      // `open = !disabled && openState`, so disabling would force-close a
      // controlled `open` the consumer is deliberately showing.
      disabled={isDisabled || (open === undefined && clickLatched)}
      open={open}
      trackCursorAxis="none"
      onOpenChange={handleOpenChange}
    >
      <BaseTooltip.Trigger
        ref={isFollowCursor ? cursorAnchor.triggerRefCallback : undefined}
        closeDelay={closeDelay}
        closeOnClick={closeOnClick}
        delay={delay}
        render={children}
        onBlur={releaseClickLatch}
        onClick={armClickLatch}
        onPointerLeave={releaseClickLatch}
      />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          align={align}
          anchor={positionerAnchor}
          className="z-tooltip"
          side={placement}
          sideOffset={sideOffset}
        >
          <BaseTooltip.Popup
            className={cn(
              typography['tooltip/label'],
              'rounded-sm border border-secondary-main bg-paper-elevation4 p-2 text-text-primary shadow-8',
              className,
            )}
          >
            {content}
            {arrow === true && (
              <BaseTooltip.Arrow className="flex data-[side=bottom]:top-[-11px] data-[side=left]:right-[-11px] data-[side=right]:left-[-11px] data-[side=top]:bottom-[-11px] data-[side=bottom]:rotate-180 data-[side=left]:-rotate-90 data-[side=right]:rotate-90 data-[side=top]:rotate-0">
                <ChevronDownIcon className="size-[20px] text-secondary-main" />
              </BaseTooltip.Arrow>
            )}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
